'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import Container from '@src/components/Shared/Container';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderButtons from '@src/components/Shared/RenderButtons';

import SocialEmbeds from '@src/components/Shared/SocialEmbeds';
import NextImage from '@src/components/Shared/NextImage';
import JsonLd from '@src/components/Shared/JsonLd';
import HubSpotNewForm from '@src/components/Sections/HubSpotNewForm/HubSpotNewForm';
import Button from '@src/components/Shared/Button';
import ScrollToWrapper from '@src/components/Shared/ScrollToWrapper/ScrollToWrapper';

import { AccessFrameworkPageClientPropTypes } from './AccessFramework.types';

const AccessFrameworkPageClient: React.FC<AccessFrameworkPageClientPropTypes> = ({ data }) => {
  const page = usePreviewContent({ content: data?.page });

  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/access-framework`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  const accessFrameworkJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });

  return (
    <>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(accessFrameworkJsonLd) }}
      />

      <div
        data-test='product-page-hero-component'
        className='relative mb-[8rem] flex flex-col items-center bg-[#EDEDED] md:h-[45rem] md:flex-row xl:mb-[7.5rem] xl:h-[68rem]'
        data-id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
      >
        <div
          className='absolute bottom-[0] right-[0] hidden md:block md:h-full md:w-full'
          style={{
            backgroundImage: `url(${page?.hero?.items?.[0]?.mediaPrimary?.url})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        ></div>
        <Container className='mb-[3.2rem]! mt-[5.6rem] w-full!'>
          <SectionHeader
            className='md:max-w-[60rem] xl:max-w-7xl'
            title={page?.hero?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName='mb-[3.2rem] text-[5rem] leading-[6.2rem] md:mb-[2.4rem] md:tracking-[-0.096rem] xl:text-[5.9rem] xl:leading-[7.3rem]'
            childrenClassName='relative'
          >
            <RenderBodyCopy
              className='max-w-[52rem]  font-light leading-[3.36rem] tracking-[-0.0342rem] md:max-w-[50rem] md:text-left md:leading-[3rem] md:tracking-[-0.036rem] lg:text-[2.5rem] lg:leading-[3.6rem] [&>p]:text-[1.9rem]! md:[&>p]:text-[2.1rem]!'
              bodyCopy={page?.hero?.items?.[0]?.bodyCopy?.json?.content}
            />
          </SectionHeader>
        </Container>

        <NextImage
          className='w-full md:hidden'
          src={page?.hero?.items?.[0]?.mediaSecondary?.url}
          alt={page?.hero?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.hero?.items?.[0]?.mediaSecondary?.width /
            page?.hero?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.hero?.items?.[0]?.mediaSecondary?.width}
        />
      </div>

      <Container>
        <SectionHeader
          title={page?.cmsAccess?.items?.[0]?.title}
          pretitle={page?.cmsAccess?.items?.[0]?.preTitle}
          titleClassName='mb-[2rem] text-[2.8rem] font-light leading-[3.2rem] md:leading-[4.6rem] md:mb-[2.4rem] xl:text-[4.8rem] xl:leading-[5rem] 2xl:max-w-[90rem] '
          align='center'
          childrenClassName='max-w-[94.2rem]!'
        >
          <RenderBodyCopy bodyCopy={page?.cmsAccess?.items?.[0]?.bodyCopy?.json?.content} />
          <div className='mt-[3rem] space-y-[1rem] sm:flex sm:flex-row sm:items-center sm:gap-[1.6rem] sm:space-y-[unset] md:mx-auto md:justify-center [&>div>a]:w-full'>
            <Button
              primary
              key={page?.cmsAccess?.items?.[0]?.buttonCollection?.items?.[0]?.sys?.id}
              btnText={page?.cmsAccess?.items?.[0]?.buttonCollection?.items?.[0]?.text}
              href={page?.cmsAccess?.items?.[0]?.buttonCollection?.items?.[0]?.url}
            />

            <ScrollToWrapper href={'#contact'}>
              <span className='common-btn-transition relative bottom-[0.1rem] flex h-[4.4rem] cursor-pointer items-center justify-center rounded-[0.6rem] border-[0.1rem] border-primary px-[2rem] text-center text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#793300]! text-primary! no-underline! transition-colors duration-300 ease-in-out before:absolute before:z-20 before:w-full before:px-[2rem] before:text-center before:font-normal before:text-primary before:content-[attr(data-pseudo)]! hover:border-primary--v2  hover:text-primary--v2 hover:before:text-primary--v2'>
                {page?.cmsAccess?.items?.[0]?.buttonCollection?.items?.[1]?.text}
              </span>
            </ScrollToWrapper>
          </div>
        </SectionHeader>
      </Container>

      <Container
        className='flex flex-col'
        id={page?.accessRequirement?.items?.[0]?.scrollTo?.slice(1)}
      >
        <SectionHeader
          className='mt-[2.4rem] justify-center md:mb-[4.8rem] lg:mb-[2.4rem] lg:pb-[unset]'
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          pretitle={page?.accessRequirement?.items?.[0]?.preTitle}
          title={page?.accessRequirement?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[1.8rem] md:mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light inline-block'
          align='center'
        >
          <>
            <RenderBodyCopy
              bodyCopy={page?.accessRequirement?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        </SectionHeader>

        <SocialEmbeds
          className='-order-1 overflow-hidden rounded-[0.6rem] text-center shadow-[0px_5px_15px_rgba(0,0,0,0.35)] md:order-1 md:mx-auto md:h-full md:w-full md:max-w-[109rem]'
          data={page?.accessRequirement?.items?.[0]?.extraAssetsCollection?.items?.[0]}
        />
      </Container>

      <div className='relative'>
        <Container className='flex flex-col gap-[0] overflow-visible! lg:flex-row lg:items-center lg:gap-[6.4rem] lg:pb-[8rem] lg:pt-[10rem] 3xl:pb-[10rem] 3xl:pt-[12rem]'>
          <div className='relative order-1 mx-[-2.4rem] mb-[2.4rem] mt-[-10rem] max-w-[98.8rem] md:mx-[-6.3rem] lg:mx-[6.4rem] lg:hidden'>
            <NextImage
              className='relative z-1 block w-full'
              src={page?.rightQuestion?.items?.[0]?.mediaSecondary?.url}
              alt={page?.rightQuestion?.items?.[0]?.mediaSecondary?.description}
              aspectRatio={
                page?.rightQuestion?.items?.[0]?.mediaSecondary?.width /
                page?.rightQuestion?.items?.[0]?.mediaSecondary?.height
              }
              width={page?.rightQuestion?.items?.[0]?.mediaSecondary?.width}
            />
          </div>

          <NextImage
            className='order-1 hidden lg:block lg:w-[50%] lg:scale-[1.3] 3xl:scale-[1.2]'
            src={page?.rightQuestion?.items?.[0]?.mediaPrimary?.url}
            alt={page?.rightQuestion?.items?.[0]?.mediaPrimary?.description}
            aspectRatio={
              page?.rightQuestion?.items?.[0]?.mediaPrimary?.width /
              page?.rightQuestion?.items?.[0]?.mediaPrimary?.height
            }
            width={page?.rightQuestion?.items?.[0]?.mediaPrimary?.width}
          />

          <SectionHeader
            className={`relative z-5 lg:w-[50%] lg:py-[4rem]`}
            pretitle={page?.rightQuestion?.items?.[0]?.preTitle}
            preTitleClassName='mb-[1.6rem] font-normal'
            title={page?.rightQuestion?.items?.[0]?.title}
            titleClassName={`text-[2.8rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5.2rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy
              className='max-w-[52rem]'
              bodyCopy={page?.rightQuestion?.items?.[0]?.bodyCopy?.json?.content}
            />
            <div className='mt-[3rem] space-y-[1rem] sm:flex sm:flex-row sm:gap-[1.6rem] sm:space-y-[unset] md:mx-auto [&>div>a]:w-full'>
              <Button
                primary
                key={page?.rightQuestion?.items?.[0]?.buttonCollection?.items?.[0]?.sys?.id}
                btnText={page?.rightQuestion?.items?.[0]?.buttonCollection?.items?.[0]?.text}
                href={page?.rightQuestion?.items?.[0]?.buttonCollection?.items?.[0]?.url}
              />

              <ScrollToWrapper href={'#contact'}>
                <span className='common-btn-transition relative bottom-[0.1rem] flex h-[4.4rem] cursor-pointer items-center justify-center rounded-[0.6rem] border-[0.1rem] border-primary px-[2rem] text-center text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#793300]! text-primary! no-underline! transition-colors duration-300 ease-in-out before:absolute before:z-20 before:w-full before:px-[2rem] before:text-center before:font-normal before:text-primary before:content-[attr(data-pseudo)]! hover:border-primary--v2  hover:text-primary--v2 hover:before:text-primary--v2'>
                  {page?.rightQuestion?.items?.[0]?.buttonCollection?.items?.[1]?.text}
                </span>
              </ScrollToWrapper>
            </div>
          </SectionHeader>
        </Container>

        <div
          className='absolute right-[0] top-[50%] hidden -translate-y-1/2  lg:block lg:h-full lg:w-full lg:max-w-[160rem]'
          style={{
            backgroundImage: `url(${page?.rightQuestion?.items?.[0]?.mediaTertiary?.url})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        />
      </div>

      <Container className='flex flex-col py-[6rem] lg:py-[9rem]' background='bg-[#F9F9F9]'>
        <SectionHeader
          className='order-2 md:-order-1 md:mb-[2.4rem]'
          title={page?.reportRequirement?.items?.[0]?.title}
          pretitle={page?.reportRequirement?.items?.[0]?.preTitle}
          titleClassName='mb-[1.8rem] lg:max-w-[40rem] text-[2.8rem] font-light leading-[3.3rem] md:leading-[3.2rem] md:mb-[2.4rem] xl:text-[4.8rem] xl:leading-[5rem] 2xl:max-w-[90rem]'
          align='center'
        >
          <RenderBodyCopy bodyCopy={page?.reportRequirement?.items?.[0]?.bodyCopy?.json?.content} />
          <RenderButtons
            className='md:mx-auto md:max-w-[6.4rem] md:justify-center'
            items={page?.reportRequirement?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <NextImage
          className='order-1 mx-auto mb-[2.4rem] max-w-[98.8rem]'
          src={page?.reportRequirement?.items?.[0]?.mediaPrimary?.url}
          alt={page?.reportRequirement?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.reportRequirement?.items?.[0]?.mediaPrimary?.width /
            page?.reportRequirement?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.reportRequirement?.items?.[0]?.mediaPrimary?.width}
        />
      </Container>

      <Container
        className='flex flex-col lg:flex-row lg:gap-[6.4rem]'
        id={page?.patientApp?.items?.[0]?.scrollTo?.slice(1)}
      >
        <SectionHeader
          className='order-2 mt-[3rem]  justify-center pb-[3.3rem] md:mb-[3.4rem] lg:order-3 lg:w-[50%] lg:pb-[unset]'
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          pretitle={page?.patientApp?.items?.[0]?.preTitle}
          title={page?.patientApp?.items?.[0]?.title}
          titleClassName='text-[2.8rem] lg:max-w-[45.5rem] xl:text-[4.8rem] mb-[1.8rem] md:mb-[2.4rem] leading-[3.2rem] md:leading-[4rem] xl:leading-[5rem] font-light inline-block'
        >
          <RenderBodyCopy
            className='mb-[4.8rem] lg:mb-[0] lg:max-w-[50.6rem]'
            bodyCopy={page?.patientApp?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons items={page?.patientApp?.items?.[0]?.buttonCollection?.items} />
        </SectionHeader>

        <div className='order-1 flex items-center overflow-hidden lg:-order-1! lg:h-full lg:w-[50%]'>
          <SocialEmbeds
            className='
      w-full
      [&_iframe]:h-full
      [&_iframe]:origin-center
      lg:[&_iframe]:w-[130%]
      lg:[&_iframe]:max-w-none
      lg:[&_iframe]:translate-x-[10%]
      lg:[&_iframe]:scale-[1.3]
      [&_video]:h-full
      [&_video]:origin-center
      lg:[&_video]:w-[130%]
      lg:[&_video]:max-w-none
      lg:[&_video]:translate-x-[10%]
      lg:[&_video]:scale-[1.3]
    '
            data={page?.patientApp?.items?.[0]?.extraAssetsCollection?.items?.[0]}
          />
        </div>
      </Container>

      <Container className='mb-[0]! py-[6rem] lg:py-[9rem]' background='bg-[#F9F9F9] h-[56%]'>
        <SectionHeader
          className='order-2 mb-[3.4rem] justify-center lg:order-3 lg:mb-[6.4rem]'
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          title={page?.getInTouch?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light inline-block'
          align='center'
        >
          <RenderBodyCopy
            className='mb-[4.8rem]'
            bodyCopy={page?.getInTouch?.items?.[0]?.bodyCopy?.json?.content}
          />
        </SectionHeader>

        <div
          data-id='contact'
          className='mx-auto max-w-[109rem] overflow-hidden rounded-[3.2rem] shadow-[0px_3.63px_3.63px_0px_rgba(0,0,0,0.25)]'
        >
          <HubSpotNewForm
            className='rounded-[3.2rem] bg-[#fff]'
            portalId={page?.getInTouch?.items?.[0]?.portalId}
            formId={page?.getInTouch?.items?.[0]?.formId}
            redirectUrl={page?.getInTouch?.items?.[0]?.redirectTo}
            formTarget='access-framework-form'
            region='na1'
            scriptSrc='https://js.hsforms.net/forms/embed/developer/22106570.js'
            submitButtonPosition='right'
          />
        </div>
      </Container>
    </>
  );
};

export default AccessFrameworkPageClient;
